import { reactive, computed, watch } from "vue";

export function useZoom({ zoomWrapper, refreshSnapshot }) {
    const state = reactive({
        // zoom page
        isZoomActive: false,
        zoomValue: 100,
        zoomStep: 5,
        zoomMin: 100,
        zoomMax: 200,
        // zoom module
        isModuleZoomActive: false,
        moduleZoomValue: 100,
        moduleZoomStep: 5,
        moduleZoomMin: 100,
        moduleZoomMax: 150,
        // zoom texte
        isTextZoomActive: false,
        textZoomValue: 100,
        textZoomMin: 100,
        textZoomMax: 250,
    });

    // --- Computed slider backgrounds ---
    const pageZoomSliderBg = computed(() => {
        const val = state.isZoomActive ? state.zoomValue : 100;
        const pct = ((val - state.zoomMin) / (state.zoomMax - state.zoomMin)) * 100;
        return `linear-gradient(to right, #1e1e1e ${pct}%, #d0d0d0 ${pct}%)`;
    });
    const moduleZoomSliderBg = computed(() => {
        const val = state.isModuleZoomActive ? state.moduleZoomValue : 100;
        const pct = ((val - state.moduleZoomMin) / (state.moduleZoomMax - state.moduleZoomMin)) * 100;
        return `linear-gradient(to right, #1e1e1e ${pct}%, #d0d0d0 ${pct}%)`;
    });
    const textZoomSliderBg = computed(() => {
        const val = state.isTextZoomActive ? state.textZoomValue : 100;
        const pct = ((val - state.textZoomMin) / (state.textZoomMax - state.textZoomMin)) * 100;
        return `linear-gradient(to right, #1e1e1e ${pct}%, #d0d0d0 ${pct}%)`;
    });

    // --- DOM helpers ---
    function getModuleContainer() { return document.querySelector(".tenjityModule-container"); }

    function compensateContentHeight(scale) {
        const container = getModuleContainer();
        if (!container) return;
        const content = container.querySelector(".tenjityModule-content");
        if (content) content.style.setProperty("max-height", `${90 / scale}vh`);
    }

    // --- Page zoom ---
    function applyZoom() {
        const appContainer = document.querySelector(zoomWrapper);
        if (!appContainer) return;
        if (typeof document.documentElement.style.zoom === "undefined") {
            const scale = state.zoomValue / 100;
            appContainer.style.transform = `scale(${scale})`;
            appContainer.style.transformOrigin = "top left";
            appContainer.style.width = `${100 / scale}%`;
            appContainer.style.height = `${100 / scale}%`;
            document.body.style.overflow = "auto";
        } else {
            appContainer.style.zoom = `${state.zoomValue}%`;
        }
        refreshSnapshot?.(80);
    }

    function removeZoom() {
        const appContainer = document.querySelector(zoomWrapper);
        if (!appContainer) return;
        appContainer.style.removeProperty("transform");
        appContainer.style.removeProperty("transform-origin");
        appContainer.style.removeProperty("width");
        appContainer.style.removeProperty("height");
        appContainer.style.removeProperty("zoom");
        document.body.style.removeProperty("overflow");
    }

    function toggleZoom() { state.isZoomActive = !state.isZoomActive; }

    function onPageZoomSlider(event) {
        const val = parseInt(event.target.value);
        if (val <= 100) { state.isZoomActive = false; state.zoomValue = 100; }
        else { state.zoomValue = Math.min(val, state.zoomMax); state.isZoomActive = true; }
    }

    function resetPageZoom() { state.isZoomActive = false; state.zoomValue = 100; }

    function applyProfileZoom(value) {
        // Multi-profil : on garde le zoom le plus élevé demandé parmi les profils actifs
        state.zoomValue = Math.min(Math.max(state.zoomValue, value), state.zoomMax);
        state.isZoomActive = true;
        applyZoom();
    }

    // --- Module zoom ---
    function applyModuleZoom() {
        const container = getModuleContainer();
        if (!container) return;
        const scale = state.moduleZoomValue / 100;
        if (typeof container.style.zoom !== "undefined") {
            container.style.zoom = `${state.moduleZoomValue}%`;
            container.style.removeProperty("transform");
            container.style.removeProperty("transform-origin");
        } else {
            container.style.transform = `scale(${scale})`;
            container.style.transformOrigin = "bottom left";
        }
        document.documentElement.style.setProperty("--tenjity-zoom-scale", scale);
        compensateContentHeight(scale);
        refreshSnapshot?.(50);
    }

    function removeModuleZoom() {
        const container = getModuleContainer();
        if (!container) return;
        container.style.removeProperty("zoom");
        container.style.removeProperty("transform");
        container.style.removeProperty("transform-origin");
        document.documentElement.style.removeProperty("--tenjity-zoom-scale");
        const content = container.querySelector(".tenjityModule-content");
        if (content) content.style.removeProperty("max-height");
        refreshSnapshot?.(50);
    }

    function toggleModuleZoom() { state.isModuleZoomActive = !state.isModuleZoomActive; }

    function onModuleZoomSlider(event) {
        const val = parseInt(event.target.value);
        if (val <= 100) { state.isModuleZoomActive = false; state.moduleZoomValue = 100; }
        else { state.moduleZoomValue = Math.min(val, state.moduleZoomMax); state.isModuleZoomActive = true; }
    }

    function resetModuleZoom() { state.isModuleZoomActive = false; state.moduleZoomValue = 100; }

    function applyProfileModuleZoom(value) {
        // Multi-profil : on garde le zoom le plus élevé demandé parmi les profils actifs
        state.moduleZoomValue = Math.min(Math.max(state.moduleZoomValue, value), state.moduleZoomMax);
        state.isModuleZoomActive = true;
        applyModuleZoom();
    }

    // --- Text zoom ---
    function applyTextZoom() {
        const wrapper = document.querySelector(zoomWrapper);
        if (!wrapper) return;
        wrapper.style.fontSize = `${state.textZoomValue}%`;
        const styleId = "tenjity-text-zoom";
        document.getElementById(styleId)?.remove();
        const style = document.createElement("style");
        style.id = styleId;
        style.textContent = `${zoomWrapper} :is(img, video, iframe, svg, canvas, input, select, textarea, button) { font-size: revert }`;
        document.head.appendChild(style);
        refreshSnapshot?.(80);
    }

    function removeTextZoom() {
        const wrapper = document.querySelector(zoomWrapper);
        if (!wrapper) return;
        wrapper.style.fontSize = "";
        document.getElementById("tenjity-text-zoom")?.remove();
        refreshSnapshot?.(80);
    }

    function onTextZoomSlider(event) {
        const val = parseInt(event.target.value);
        if (val <= 100) { resetTextZoom(); }
        else { state.textZoomValue = val; state.isTextZoomActive = true; window.localStorage.setItem("tenjityTextZoom", String(val)); }
    }

    function setTextZoomLevel(level) {
        if (level <= 100) { resetTextZoom(); }
        else {
            state.textZoomValue = Math.min(level, state.textZoomMax);
            state.isTextZoomActive = true;
            window.localStorage.setItem("tenjityTextZoom", String(state.textZoomValue));
        }
    }

    function resetTextZoom() {
        state.isTextZoomActive = false;
        state.textZoomValue = 100;
        window.localStorage.removeItem("tenjityTextZoom");
    }

    function applyProfileTextZoom(value) {
        // Multi-profil : on garde le zoom le plus élevé demandé parmi les profils actifs
        state.textZoomValue = Math.max(state.textZoomValue, value);
        state.isTextZoomActive = true;
        applyTextZoom();
    }

    // --- Reset global ---
    function reset() {
        resetPageZoom();
        removeZoom();
        resetModuleZoom();
        removeModuleZoom();
        resetTextZoom();
        removeTextZoom();
    }

    // --- Watchers ---
    watch(() => state.isZoomActive, (val) => { val ? applyZoom() : removeZoom(); });
    watch(() => state.zoomValue, () => { if (state.isZoomActive) applyZoom(); });
    watch(() => state.isModuleZoomActive, (val) => {
        if (!val) { removeModuleZoom(); return; }
        if (getModuleContainer()?.classList.contains("tenjityModule-container-active")) applyModuleZoom();
    });
    watch(() => state.moduleZoomValue, () => { if (state.isModuleZoomActive) applyModuleZoom(); });
    watch(() => state.isTextZoomActive, (val) => { val ? applyTextZoom() : removeTextZoom(); });
    watch(() => state.textZoomValue, () => { if (state.isTextZoomActive) applyTextZoom(); });

    return {
        state,
        pageZoomSliderBg,
        moduleZoomSliderBg,
        textZoomSliderBg,
        // Page zoom
        applyZoom,
        removeZoom,
        toggleZoom,
        onPageZoomSlider,
        resetPageZoom,
        applyProfileZoom,
        // Module zoom
        applyModuleZoom,
        removeModuleZoom,
        toggleModuleZoom,
        onModuleZoomSlider,
        resetModuleZoom,
        applyProfileModuleZoom,
        // Text zoom
        applyTextZoom,
        removeTextZoom,
        onTextZoomSlider,
        setTextZoomLevel,
        resetTextZoom,
        applyProfileTextZoom,
        // Global
        reset,
    };
}
